import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AuthApi, UnauthApi } from '@/api';
import { useStatusBarColor } from '@/hooks/useStatusBarColor';
import { useAuthStore } from '@/stores/AuthStore';
import type { DeviceTokenData } from '@/stores/DeviceStore';
import { STATUS_COLOR, useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { isForceUpdateRequired } from '../_utils/forceUpdate';
import { MESSAGE_KEYWORD } from '../_utils/reserved-keywords';
import { useHandleApp } from './useHandleApp';
export const useMountAppData = () => {
  const $ = _c(45);
  const [osType, setAppVersion, tempDeviceTokenData, setTempDeviceTokenData, deviceTokenData, setDeviceTokenData, webviewType, setAppModal] = useDeviceInfoStore(useShallow(_temp));
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: MESSAGE_KEYWORD.VERSION
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    request: requestAppVersion,
    response: responseAppVersion
  } = useHandleApp(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: MESSAGE_KEYWORD.NOTIFICATION
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    request: requestNotification,
    response: responseNotification
  } = useHandleApp(t1);
  let t2;
  let t3;
  if ($[2] !== osType || $[3] !== requestAppVersion) {
    t2 = () => {
      if (!osType) {
        return;
      }
      requestAppVersion();
    };
    t3 = [requestAppVersion, osType];
    $[2] = osType;
    $[3] = requestAppVersion;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  const isFirstLoading = responseAppVersion?.isSuccess && responseAppVersion.data ? responseAppVersion.data.isFirstLoading : "Y";
  let t4;
  let t5;
  if ($[6] !== setAppModal || $[7] !== webviewType) {
    t4 = () => {
      if (webviewType !== WEBVIEW_TYPE.WELLO_APP) {
        return;
      }
      if (isForceUpdateRequired(navigator.userAgent)) {
        setAppModal({
          type: "forceUpdate",
          isOpened: true
        });
      }
    };
    t5 = [webviewType, setAppModal];
    $[6] = setAppModal;
    $[7] = webviewType;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[10] !== responseAppVersion || $[11] !== setAppVersion) {
    t6 = () => {
      if (responseAppVersion?.isSuccess && responseAppVersion?.data) {
        const {
          version
        } = responseAppVersion.data;
        setAppVersion(version);
      }
    };
    t7 = [responseAppVersion, setAppVersion];
    $[10] = responseAppVersion;
    $[11] = setAppVersion;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[14] !== deviceTokenData || $[15] !== isFirstLoading || $[16] !== osType || $[17] !== requestNotification) {
    t8 = () => {
      if (!osType) {
        return;
      }
      if (!deviceTokenData && isFirstLoading === "N") {
        requestNotification();
      }
    };
    t9 = [deviceTokenData, requestNotification, isFirstLoading, osType];
    $[14] = deviceTokenData;
    $[15] = isFirstLoading;
    $[16] = osType;
    $[17] = requestNotification;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  const isLogin = useAuthStore(_temp2);
  const {
    toastError
  } = useToast();
  let t10;
  if ($[20] !== isLogin) {
    t10 = requestBody => isLogin ? AuthApi["post:/wello/v2/auth/wello-device-info"]({
      body: {
        ...requestBody
      }
    }) : UnauthApi["post:/wello/v2/allows/wello-device-info"]({
      body: {
        ...requestBody
      }
    });
    $[20] = isLogin;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== setDeviceTokenData || $[23] !== tempDeviceTokenData) {
    t11 = () => {
      if (tempDeviceTokenData) {
        setDeviceTokenData(tempDeviceTokenData);
      }
    };
    $[22] = setDeviceTokenData;
    $[23] = tempDeviceTokenData;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== toastError) {
    t12 = error => toastError({
      error
    });
    $[25] = toastError;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t12) {
    t13 = {
      mutationFn: t10,
      onSuccess: t11,
      onError: t12
    };
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const {
    mutate: saveDeviceTokenMutate
  } = useMutation(t13);
  let t14;
  if ($[31] !== isLogin || $[32] !== responseNotification || $[33] !== setTempDeviceTokenData) {
    t14 = () => {
      if (!responseNotification || isLogin === undefined) {
        return;
      }
      const {
        isSuccess,
        data
      } = responseNotification;
      if (!isSuccess || !data) {
        return;
      }
      setTempDeviceTokenData(data);
    };
    $[31] = isLogin;
    $[32] = responseNotification;
    $[33] = setTempDeviceTokenData;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== isLogin || $[36] !== responseNotification || $[37] !== saveDeviceTokenMutate || $[38] !== setTempDeviceTokenData) {
    t15 = [isLogin, responseNotification, saveDeviceTokenMutate, setTempDeviceTokenData];
    $[35] = isLogin;
    $[36] = responseNotification;
    $[37] = saveDeviceTokenMutate;
    $[38] = setTempDeviceTokenData;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  useEffect(t14, t15);
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      color: STATUS_COLOR.DEFAULT
    };
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  useStatusBarColor(t16);
  let t17;
  let t18;
  if ($[41] !== saveDeviceTokenMutate || $[42] !== tempDeviceTokenData) {
    t17 = () => {
      if (tempDeviceTokenData) {
        saveDeviceTokenMutate(tempDeviceTokenData);
      }
    };
    t18 = [saveDeviceTokenMutate, tempDeviceTokenData];
    $[41] = saveDeviceTokenMutate;
    $[42] = tempDeviceTokenData;
    $[43] = t17;
    $[44] = t18;
  } else {
    t17 = $[43];
    t18 = $[44];
  }
  useEffect(t17, t18);
};
function _temp(state) {
  return [state.osType, state.setAppVersion, state.tempDeviceTokenData, state.setTempDeviceTokenData, state.deviceTokenData, state.setDeviceTokenData, state.webviewType, state.setAppModal];
}
function _temp2(state_0) {
  return state_0.isLogin;
}